// Pulse zoom (user, 9 Oct 2026: "the user should clearly understand what he is looking at — echo, click or snap").
// 8 ms of the recording around the picked pulse (from its click when it is an echo), every pulse in it marked and named:
// the click, its echoes (delay, dB) and isolated pulses. Plus the short guide "What am I looking at?".
import React, { useEffect, useMemo } from 'react';
import { ANIMAL_LABELS, LABEL, echoParent } from './pulsedModel';

const W = 1400; const H = 250; const L = 60; const R = 20; const PW = W - L - R; const TOP = 70; const BOT = H - 34;
const ECHO_COL = '#d4880f';
const PRE = 0.0015; const POST = 0.0065;

function roleOf(q, clicks, byNo, trainOf) {
  if (q.train !== null && ANIMAL_LABELS.has(q.label)) {
    const tr = trainOf.get(q.train);
    return { kind: 'click', text: `#${q.no} CLICK · ${tr && tr.type === 'burst pulse' ? 'burst' : 'train'} #${q.train}` };
  }
  const ep = echoParent(clicks, q, byNo);
  if (ep) return { kind: 'echo', parent: ep.parent, text: `#${q.no} ECHO of #${ep.parent.no} · +${ep.dtMs.toFixed(2)} ms${ep.dB !== null ? ` · ${ep.dB.toFixed(0)} dB` : ''}` };
  if (q.label === 'echo') return { kind: 'echo', text: `#${q.no} echo` };
  if (q.label === 'shrimp') return { kind: 'snap', text: `#${q.no} isolated pulse (snap?)` };
  return { kind: 'other', text: `#${q.no} ${(LABEL[q.label]?.[0] || q.label).toLowerCase()}` };
}

export default function PulseZoom({ clicks, byNo, no, sig, ensureSignals, colOf, trains }) {
  useEffect(() => { if (!sig) ensureSignals(); }, [sig, ensureSignals]);
  const trainOf = useMemo(() => new Map(trains.map((t) => [t.id, t])), [trains]);
  const k = byNo.get(no);
  if (!k) return null;
  const rk = roleOf(k, clicks, byNo, trainOf);
  const anchor = rk.kind === 'echo' && rk.parent ? rk.parent : k;
  const a = anchor.t - PRE; const b = anchor.t + POST;
  const X = (t) => L + ((t - a) / (b - a)) * PW;
  const inWin = clicks.filter((q) => q.t >= a && q.t <= b).sort((p, q) => p.t - q.t);
  const roles = inWin.map((q) => ({ q, r: roleOf(q, clicks, byNo, trainOf) }));
  const col = (q, r) => (r.kind === 'click' ? colOf.get(q.train) : r.kind === 'echo' ? ECHO_COL : '#8a8a8a');

  let wave = null;
  if (sig) {
    const i0 = Math.max(0, Math.floor(a * sig.fs)); const i1 = Math.min(sig.y.length, Math.ceil(b * sig.fs));
    let amp = 1e-12; for (let i = i0; i < i1; i += 1) amp = Math.max(amp, Math.abs(sig.y[i]));
    const mid = (TOP + BOT) / 2; const half = (BOT - TOP) / 2;
    const step = Math.max(1, Math.floor((i1 - i0) / 3000));
    const pts = []; for (let i = i0; i < i1; i += step) pts.push(`${X(i / sig.fs).toFixed(1)},${(mid - (sig.y[i] / amp) * half).toFixed(1)}`);
    wave = pts.join(' ');
  }
  // the verdict in words for the picked pulse
  const verdict = rk.kind === 'click' ? `#${k.no} is a CLICK: it is the first and loudest arrival.${roles.some(({ r }) => r.kind === 'echo') ? ' The orange pulses after it are its echoes — the same click reflected by the surface / bottom, arriving later and weaker.' : ''}`
    : rk.kind === 'echo' && rk.parent ? `#${k.no} is an ECHO of #${rk.parent.no}: it arrives ${(k.t - rk.parent.t) * 1000 > 0 ? `${((k.t - rk.parent.t) * 1000).toFixed(2)} ms later` : 'just after'} and weaker — the same click by a longer path. Keep #${rk.parent.no} in the train, not this one.`
      : rk.kind === 'snap' ? `#${k.no} has no louder pulse just before it and is in no train: an isolated pulse — usually a snapping shrimp. If it sits exactly in a train's rhythm, it can still be a click: join it.`
        : `#${k.no}: ${rk.text.replace(`#${k.no} `, '')}.`;
  const ticks = []; for (let ms = -1; ms <= 6; ms += 1) ticks.push(ms);
  return (
    <div className="pz">
      <div className="hcr-row-between"><strong>Pulse zoom · 8 ms around {anchor === k ? `#${k.no}` : `#${anchor.no} (the click of #${k.no})`}</strong>
        {!sig && <span className="ct-muted">loading the recording…</span>}</div>
      <p className="pz-verdict">{verdict}</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="pz-svg" role="img" aria-label="Waveform 8 ms around the pulse, each pulse named">
        <rect width={W} height={H} fill="#fff" />
        <rect x={L} y={TOP} width={PW} height={BOT - TOP} fill="#fafafa" stroke="#ccc" />
        {wave && <polyline points={wave} fill="none" stroke="#333" strokeWidth={1} />}
        {roles.map(({ q, r }, i) => {
          const x = X(q.t); const c = col(q, r); const y = 16 + (i % 3) * 18; const on = q.no === k.no;
          return (<g key={q.no}>
            <line x1={x} x2={x} y1={y + 4} y2={BOT} stroke={c} strokeWidth={on ? 3 : 1.6} strokeDasharray={r.kind === 'echo' ? '6 4' : (r.kind === 'click' ? 'none' : '2 4')} />
            <text x={Math.min(W - R - 4, x + 4)} y={y} fontSize="15" fill={c} fontWeight={on || r.kind === 'click' ? 700 : 400} textAnchor={x > W - 360 ? 'end' : 'start'}>{r.text}</text>
          </g>); })}
        {ticks.map((ms) => { const x = X(anchor.t + ms / 1000); return (<g key={ms}><line x1={x} x2={x} y1={BOT} y2={BOT + 5} stroke="#000" /><text x={x} y={BOT + 21} fontSize="15" textAnchor="middle">{ms}</text></g>); })}
        <text x={W - R} y={H - 2} fontSize="14" textAnchor="end">ms after #{anchor.no}</text>
      </svg>
    </div>);
}

/** Short guide above the editor: click, echo or snap — and how to edit. */
export function WhatAmISeeing() {
  return (
    <details className="pz-guide">
      <summary>What am I looking at? Click, echo or snap</summary>
      <div className="pz-guide-body">
        <svg viewBox="0 0 640 120" className="pz-guide-svg" role="img" aria-label="Sketch: a click, its echo a few ms later, and an isolated snap">
          <line x1="20" x2="620" y1="70" y2="70" stroke="#bbb" />
          <line x1="80" x2="80" y1="12" y2="108" stroke="#1f6fd1" strokeWidth="5" /><text x="88" y="22" fontSize="14" fill="#1f6fd1" fontWeight="700">click</text>
          <line x1="150" x2="150" y1="44" y2="96" stroke={ECHO_COL} strokeWidth="4" strokeDasharray="5 3" /><text x="158" y="60" fontSize="14" fill={ECHO_COL}>echo</text><text x="158" y="76" fontSize="12" fill={ECHO_COL}>later, weaker</text>
          <line x1="300" x2="300" y1="12" y2="108" stroke="#1f6fd1" strokeWidth="5" /><text x="308" y="22" fontSize="14" fill="#1f6fd1" fontWeight="700">next click</text>
          <line x1="370" x2="370" y1="44" y2="96" stroke={ECHO_COL} strokeWidth="4" strokeDasharray="5 3" />
          <line x1="530" x2="530" y1="20" y2="104" stroke="#8a8a8a" strokeWidth="5" /><text x="538" y="30" fontSize="14" fill="#666">snap</text>
          <text x="80" y="118" fontSize="12" fill="#444">←—— the interval (rhythm) ——→</text>
        </svg>
        <ul>
          <li><strong>Click</strong>: the FIRST and usually loudest arrival. A dolphin's clicks come in a rhythm: the interval changes smoothly from click to click.</li>
          <li><strong>Echo</strong>: the same click again, reflected by the surface or the bottom — always AFTER its click (0.1–5 ms here), weaker, and at about the same delay after every click of the train (the delay drifts slowly as the animal moves). Polarity is no reliable sign: near full scale the shapes are distorted.</li>
          <li><strong>Snap</strong> (isolated pulse): no rhythm, no fixed partner. Snapping shrimp; very loud ones have their own echoes too.</li>
          <li>In the interval panel, a zig-zag of short / long intervals means the train holds echoes. Select the train: <em>Swap … echoes for their clicks</em> fixes it.</li>
        </ul>
        <ul>
          <li><strong>Add a pulse to a train / burst</strong>: Extend, click the train's bar, click the pulse (or the waveform where a pulse is missing). Several: Select, drag across them, Join to …, Join.</li>
          <li><strong>Take a pulse out</strong>: select it, <em>Take out of train</em> (it stays, unassigned). <em>Remove</em> deletes the detection — for noise only.</li>
          <li><strong>See any pulse up close</strong>: click it — the pulse zoom below the editor names every pulse in the 8 ms around it.</li>
        </ul>
      </div>
    </details>);
}
